import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import colors from 'styles/theme/colors';

export enum StateCircleState {
  DORMANT = 'dormant',
  AWOKEN = 'awoken',
  UNLEASHED = 'unleashed',
}

type StateCircleIconProps = {
  state: StateCircleState;
  width?: number;
  height?: number;
} & React.SVGProps<SVGSVGElement>;

export default function StateCircleIcon({ state, width = 20, height = 20, ...props }: StateCircleIconProps) {
  const [animationProgress, setAnimationProgress] = useState(0);
  const animationRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number>(performance.now());

  const spiralToVertex = (center: [number, number], vertex: [number, number, number], t: number): [number, number] => {
    if (t === 0) return center;
    if (t === 1) return [vertex[0], vertex[1]];

    const [cx, cy] = center;
    const [vx, vy, theta] = vertex;
    const finalRadius = Math.sqrt((vx - cx) ** 2 + (vy - cy) ** 2);
    const currentRadius = finalRadius * t;
    const currentAngle = theta * (1 - t) + Math.atan2(vy - cy, vx - cx);
    const x = cx + currentRadius * Math.cos(currentAngle);
    const y = cy + currentRadius * Math.sin(currentAngle);
    return [x, y];
  };

  useEffect(() => {
    const isCompleted = state === StateCircleState.UNLEASHED;

    if ((isCompleted && animationProgress < 1) || (!isCompleted && animationProgress > 0)) {
      lastTimeRef.current = performance.now();
      const direction = isCompleted ? 1 : -1;

      const animate = (currentTime: number) => {
        const delta = currentTime - lastTimeRef.current;
        lastTimeRef.current = currentTime;
        setAnimationProgress(Math.min(Math.max(animationProgress + (delta / 500) * direction, 0), 1));
        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    } else {
      setAnimationProgress(isCompleted ? 1 : 0);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, animationProgress]);

  const getCurrentPath = () => {
    const center: [number, number] = [10, 10];
    const verts: [number, number, number][] = [
      [6, 10.5, 3 * Math.PI],
      [9, 13.5, 2 * Math.PI],
      [14, 7.5, 1 * Math.PI],
    ];

    const animVerts = verts.map((vertex) => spiralToVertex(center, vertex, animationProgress));

    return `M ${animVerts[0][0]} ${animVerts[0][1]} L ${animVerts[1][0]} ${animVerts[1][1]} L ${animVerts[2][0]} ${animVerts[2][1]}`;
  };

  const getCurrentStrokeWidth = () => {
    // Interpolate between 10 (circle) and 2 (checkmark)
    return 10 - 8 * animationProgress;
  };

  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={width} height={height} viewBox='0 0 20 20' {...props}>
      <defs>
        <linearGradient id='state-circle-completed-gradient' x1='0%' y1='50%' x2='100%' y2='50%'>
          <stop offset='0%' stopColor='#9073CA' />
          <stop offset='50%' stopColor='#785BD4' />
          <stop offset='100%' stopColor='#593EE4' />
        </linearGradient>
        <linearGradient id='state-circle-awoken-gradient' x1='0%' y1='50%' x2='100%' y2='50%'>
          <stop stopColor='#9073CA' stopOpacity='0.5' />
          <stop offset='0.485753' stopColor='#785BD4' stopOpacity='0.75' />
          <stop offset='1' stopColor='#593EE4' />
        </linearGradient>
      </defs>

      <GrayCircle cx='10' cy='10' r='10' fill={colors.black25} $state={state} />

      <AwokenCircle cx='10' cy='10' r='10' fill='url(#state-circle-awoken-gradient)' $state={state}>
        <animateTransform
          attributeName='transform'
          attributeType='XML'
          type='rotate'
          from='0 10 10'
          to='360 10 10'
          dur='2s'
          repeatCount='indefinite'
        />
      </AwokenCircle>

      <CompletedCircle cx='10' cy='10' r='10' fill='url(#state-circle-completed-gradient)' $state={state} />

      <path
        d={getCurrentPath()}
        stroke='white'
        strokeWidth={getCurrentStrokeWidth()}
        strokeLinecap='round'
        strokeLinejoin='round'
        fill='none'
      />
    </svg>
  );
}

const GrayCircle = styled.circle<{ $state: StateCircleState }>`
  opacity: ${({ $state }) => ($state === StateCircleState.DORMANT ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const AwokenCircle = styled.circle<{ $state: StateCircleState }>`
  opacity: ${({ $state }) => ($state === StateCircleState.AWOKEN ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const CompletedCircle = styled.circle<{ $state: StateCircleState }>`
  opacity: ${({ $state }) => ($state === StateCircleState.UNLEASHED ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;
